import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { NOTIFY_LIMITS, type AccountUser, type NotificationsResponse, type NotifySettings } from '@whaletail/shared';
import { ApiError, getNotifications, saveNotifySettings, testPush } from '../api.ts';
import {
  disableOnThisDevice,
  enableOnThisDevice,
  isOnForAccount,
  pushSupport,
  rememberAlerts,
  type PushSupport,
} from '../push.ts';

interface Props {
  user: AccountUser;
  notify: (text: string) => void;
  /** Every settings state the server confirms, so the map's radius ring follows what was actually saved. */
  onChange?: (r: NotificationsResponse) => void;
}

const errText = (e: unknown, fallback: string) => (e instanceof ApiError || e instanceof Error ? e.message : fallback);

const SUPPORT_TEXT: Record<Exclude<PushSupport, 'default' | 'granted'>, string> = {
  unsupported: 'This browser cannot receive notifications.',
  'ios-needs-install':
    'On iPhone and iPad, alerts only work from the Home Screen app: tap Share, then Add to Home Screen, and open WhaleTail from there.',
  denied: 'Notifications are blocked for this site. Allow them in your browser or phone settings to get alerts here.',
};

/**
 * Nearby-whale alerts (Settings tab, under Home). Alerts are switched on and
 * off per device only — "Turn on here" in each browser — since a phone and a
 * laptop each have to subscribe on their own. The radius and the limit are
 * shared by all of the account's devices.
 */
export function NearbyAlerts({ user, notify, onChange }: Props) {
  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [support, setSupport] = useState<PushSupport>(() => pushSupport());
  const [onThisDevice, setOnThisDevice] = useState(false);

  const [radius, setRadius] = useState('');
  const [maxCount, setMaxCount] = useState('');
  const [windowHours, setWindowHours] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Through a ref so `apply` keeps a stable identity (the parent passes an inline function).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const apply = useCallback((r: NotificationsResponse) => {
    setData(r);
    onChangeRef.current?.(r);
    setRadius(String(r.settings.radiusMiles));
    setMaxCount(String(r.settings.maxCount));
    setWindowHours(String(r.settings.windowHours));
  }, []);

  useEffect(() => {
    getNotifications().then(apply).catch(() => undefined);
    // Per account: another account's kept subscription on this device is not "on" for this one.
    isOnForAccount(user.id)
      .then(setOnThisDevice)
      .catch(() => undefined);
  }, [apply, user.id]);

  /** The form's values, or an error message if any is out of range. */
  const readForm = (): NotifySettings | string => {
    const s = { radiusMiles: Number(radius), maxCount: Number(maxCount), windowHours: Number(windowHours) };
    const inRange = (v: number, r: { min: number; max: number }) => Number.isFinite(v) && v >= r.min && v <= r.max;
    if (!inRange(s.radiusMiles, NOTIFY_LIMITS.radiusMiles))
      return `Use a radius of ${NOTIFY_LIMITS.radiusMiles.min}–${NOTIFY_LIMITS.radiusMiles.max} miles.`;
    if (!Number.isInteger(s.maxCount) || !inRange(s.maxCount, NOTIFY_LIMITS.maxCount))
      return `Use ${NOTIFY_LIMITS.maxCount.min}–${NOTIFY_LIMITS.maxCount.max} notifications.`;
    if (!inRange(s.windowHours, NOTIFY_LIMITS.windowHours))
      return `Use ${NOTIFY_LIMITS.windowHours.min}–${NOTIFY_LIMITS.windowHours.max} hours.`;
    return s;
  };

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errText(e, fallback));
    } finally {
      setBusy(false);
      setSupport(pushSupport());
    }
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    const s = readForm();
    if (typeof s === 'string') return setError(s);
    void run(async () => {
      apply(await saveNotifySettings(s));
      notify('Alert settings saved.');
    }, 'Could not save your alert settings.');
  };

  const enableDevice = () =>
    void run(async () => {
      if (!data?.vapidPublicKey) throw new Error('Notifications are not set up on this server.');
      apply(await enableOnThisDevice(data.vapidPublicKey));
      // Per account, per device: resumes by itself after this account signs out and back in.
      rememberAlerts(user.id, true);
      setOnThisDevice(true);
      notify('Alerts are on for this device.');
    }, 'Could not turn on alerts here.');

  const disableDevice = () =>
    void run(async () => {
      await disableOnThisDevice(user.id);
      setOnThisDevice(false);
      apply(await getNotifications());
      notify('Alerts are off for this device.');
    }, 'Could not turn off alerts here.');

  const sendTest = () =>
    void run(async () => {
      const r = await testPush();
      // A device the push service reported gone was just removed; keep the count (and the map ring) honest.
      if (data && r.deviceCount !== data.deviceCount) apply({ ...data, deviceCount: r.deviceCount });
      if (r.sent > 0) notify(`Test sent to ${r.sent} device${r.sent === 1 ? '' : 's'}.`);
      // Kept on screen (not a toast): this is what to fix, e.g. a bad key in .env.
      if (r.errors.length) {
        const what = r.sent > 0 ? 'Some devices did not get it' : 'The test could not be delivered';
        throw new Error(`${what}: ${r.errors.join('; ')}`);
      }
      if (r.sent === 0) throw new Error('The test could not be delivered.');
    }, 'Could not send a test notification.');

  if (!data || !data.available) return null;

  const plural = (n: string, word: string) => (Number(n) === 1 ? word : `${word}s`);

  return (
    <section className="section">
      <h3>Nearby Whale Alerts</h3>
      {!user.home ? (
        <p className="muted">Set a home location above to get a notification when whales are reported near it.</p>
      ) : (
        <>
          <p className="muted">Get a notification when a new report comes in near home.</p>

          <div className="alerts-device">
            <span className="alerts-device-state">
              <strong>This device:</strong> {onThisDevice ? 'on' : 'off'}
            </span>
            {support === 'default' || support === 'granted' ? (
              onThisDevice ? (
                <button type="button" className="btn btn-quiet" onClick={disableDevice} disabled={busy}>
                  Turn off here
                </button>
              ) : (
                <button type="button" className="btn btn-primary" onClick={enableDevice} disabled={busy}>
                  Turn on here
                </button>
              )
            ) : null}
          </div>
          {support !== 'default' && support !== 'granted' && <p className="hint">{SUPPORT_TEXT[support]}</p>}

          <form onSubmit={save} className="stack">
            <label className="alerts-line">
              <span>Within</span>
              <input
                type="number"
                inputMode="decimal"
                min={NOTIFY_LIMITS.radiusMiles.min}
                max={NOTIFY_LIMITS.radiusMiles.max}
                step="any"
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                aria-label="Radius in miles"
              />
              <span>{plural(radius, 'mile')} of home</span>
            </label>
            <label className="alerts-line">
              <span>At most</span>
              <input
                type="number"
                inputMode="numeric"
                min={NOTIFY_LIMITS.maxCount.min}
                max={NOTIFY_LIMITS.maxCount.max}
                step={1}
                value={maxCount}
                onChange={(e) => setMaxCount(e.target.value)}
                aria-label="Maximum notifications"
              />
              <span>{plural(maxCount, 'notification')} every</span>
              <input
                type="number"
                inputMode="decimal"
                min={NOTIFY_LIMITS.windowHours.min}
                max={NOTIFY_LIMITS.windowHours.max}
                step="any"
                value={windowHours}
                onChange={(e) => setWindowHours(e.target.value)}
                aria-label="Hours"
              />
              <span>{plural(windowHours, 'hour')}</span>
            </label>
            {error && <p className="form-error">{error}</p>}
            <div className="row gap">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                Save
              </button>
              {data.deviceCount > 0 && (
                <button type="button" className="btn" onClick={sendTest} disabled={busy}>
                  Send Test
                </button>
              )}
            </div>
            <p className="hint">
              {data.deviceCount === 0
                ? 'Alerts are off: no device has them turned on. These settings apply to every device you turn on.'
                : `Alerts are on for ${data.deviceCount} device${data.deviceCount === 1 ? '' : 's'}. The limit counts once across all of them; reports past it still show on the map.`}
            </p>
          </form>
        </>
      )}
    </section>
  );
}
